/**
 * Which ONE pipeline action the Registry page offers for a package version,
 * derived from that version's rows (one per compiler version it was
 * attempted under). Operator report, 2026-10-12: every version row showed
 * both "Republish" and "Rebuild"; Republish of an already-published version
 * is a deliberate no-op (a build from the current compiler already exists —
 * internal/publish force.ts `republish`), yet it toasted "republish queued"
 * and nothing happened. "Don't even offer the button if I can't click it." So:
 *
 *  - in_progress (an attempt is queued or running): no action — its status
 *    and Cancel instead;
 *  - served (the registry serves one of our builds): "Rebuild with current
 *    compiler" (force `rebuild`: a NEW pinnable build `X.Y.Z-pure.<cv>`,
 *    X.Y.Z moved onto it — 2026-10-12 moving-pointer scheme);
 *  - withdrawn (rolled back to upstream): "Re-publish with current
 *    compiler" (also `rebuild` — an ordinary publish skips a withdrawn
 *    version);
 *  - unpublished (failed, cancelled or never attempted): "Retry publish" /
 *    "Publish" (force `republish`).
 *
 * The broker refuses exactly what is not offered here
 * (private/auth/src/publishRoutes.ts versionPublishState — keep the two
 * derivations in step).
 *
 * Pure functions, no React — unit-tested by versionState.test.ts.
 */

import { parseNativeTargets } from "./nativeTargets.ts";
import { parseSteps } from "./stepChecklist.ts";

/** The fields of a publish_versions row this derivation reads. */
export interface VersionRowLike {
  package_name: string;
  version: string;
  compiler_version: string | null;
  status: string;
  /** Version string the registry serves from this row's build (D1
   *  migration 0079). Survives a failed/cancelled re-run of a published
   *  row; cleared on withdraw. Absent/null on rows from older publishers —
   *  `status` is then the only evidence. */
  published_as?: string | null;
  deprecated?: string | null;
  run_mode?: string | null;
  requested_by?: string | null;
  /** JSON NativeTargetRecord[] of the row's build (migration 0080): read by
   *  `canRebenchMacos` for the darwin-arm64 record and its macBench badge. */
  native_targets?: string | null;
  /** JSON StepRecord[] checklist (migration 0083): read by
   *  `canRebenchMacos` for the `benchmark-mac` row. */
  steps?: unknown;
  updated_at: string;
}

/** internal/publish types.ts TERMINAL_STATUSES. */
export const TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  "published",
  "failed",
  "cancelled",
  "verified",
  "withdrawn",
  "superseded",
]);

export type VersionState<T extends VersionRowLike> =
  | { kind: "in_progress"; attempt: T }
  | { kind: "served"; build: T | null; deprecated: string | null }
  | { kind: "withdrawn" }
  | { kind: "unpublished"; last: T | null };

/** SQLite `datetime('now')` ("YYYY-MM-DD HH:MM:SS", UTC, no zone) or ISO. */
export function parseTimestamp(s: string): number {
  const iso = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)
    ? `${s.replace(" ", "T")}Z`
    : s;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function newestFirst<T extends VersionRowLike>(rows: T[]): T[] {
  return [...rows].sort(
    (a, b) => parseTimestamp(b.updated_at) - parseTimestamp(a.updated_at),
  );
}

/** Whether this row's build is what the registry serves for its version. */
export function servesBuild(r: VersionRowLike): boolean {
  if (r.status === "withdrawn" || r.status === "superseded") return false;
  return r.status === "published" || r.published_as != null;
}

export function deriveVersionState<T extends VersionRowLike>(
  rows: T[],
): VersionState<T> {
  const sorted = newestFirst(rows);
  const live = sorted.find((r) => !TERMINAL_STATUSES.has(r.status));
  if (live) return { kind: "in_progress", attempt: live };
  const withdrawn = sorted.some((r) => r.status === "withdrawn");
  const build =
    sorted.find((r) => r.status === "published") ??
    sorted.find(servesBuild) ??
    null;
  if (build || (!withdrawn && sorted.some((r) => r.status === "verified"))) {
    const deprecated =
      sorted.find((r) => servesBuild(r) && r.deprecated)?.deprecated ?? null;
    return { kind: "served", build, deprecated };
  }
  if (withdrawn) return { kind: "withdrawn" };
  return { kind: "unpublished", last: sorted[0] ?? null };
}

/** Rows grouped per (package, version), keyed `${package}@${version}`. */
export function groupByVersion<T extends VersionRowLike>(
  rows: T[],
): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const r of rows) {
    const key = versionKey(r.package_name, r.version);
    const list = out.get(key);
    if (list) list.push(r);
    else out.set(key, [r]);
  }
  return out;
}

export function versionKey(pkg: string, version: string): string {
  return `${pkg}@${version}`;
}

/** The force action behind the one offered button. */
export type PipelineActionKind = "rebuild" | "republish" | "rebench-macos";

export interface PrimaryAction {
  kind: PipelineActionKind;
  label: string;
  /** Short hover text. */
  title: string;
}

/**
 * Lockfile caveat of replacing the bytes behind an existing version string.
 * Kept in ONE place on purpose. null since the moving-pointer scheme
 * (2026-10-12, internal/publish/src/versioning.ts): a rebuild publishes a
 * NEW immutable build `X.Y.Z-pure.<cv>` and moves the X.Y.Z pointer to it;
 * no published bytes change, so lockfiles never hit EINTEGRITY.
 */
export const REBUILD_LOCKFILE_WARNING: string | null = null;

/** What a rebuild does to existing installs (moving-pointer scheme; user
 *  decision 2026-10-12: X.Y.Z moves ONLY on an operator Rebuild — compiler
 *  releases just re-verify). */
export function rebuildLockfileNotes(pkg: string, version: string): string[] {
  return [
    `Rebuild moves ${version} for NEW installs only: anyone resolving ${pkg}@${version} (or a range like ^${version}) gets the new build. Existing lockfiles keep installing exactly the build they recorded — every build stays downloadable and can be pinned as ${version}-pure.<compiler>.`,
    `Exception — pnpm 11 and later verify lockfile URLs against the registry: a pnpm lockfile pinned to the previous build of ${version} fails on a fresh CI machine (ERR_PNPM_TARBALL_URL_MISMATCH) until \`pnpm update ${pkg}\` is run there. npm, yarn and bun lockfiles are unaffected.`,
  ];
}

export function primaryAction<T extends VersionRowLike>(
  state: VersionState<T>,
): PrimaryAction | null {
  switch (state.kind) {
    case "in_progress":
      return null;
    case "served":
      return {
        kind: "rebuild",
        label: "Rebuild with current compiler",
        title:
          "Build this version with the current compiler, verify it, publish it as a new pinnable build, and point the version at it",
      };
    case "withdrawn":
      return {
        kind: "rebuild",
        label: "Re-publish with current compiler",
        title:
          "This version was rolled back to upstream's original. Build it with the current compiler, verify it, and serve our build again",
      };
    case "unpublished":
      return state.last
        ? {
            kind: "republish",
            label: "Retry publish",
            title:
              "Run the pipeline again: compile → test → compile → test → compile → verify → publish",
          }
        : {
            kind: "republish",
            label: "Publish",
            title:
              "Run the pipeline: compile → test → compile → test → compile → verify → publish",
          };
  }
}

/**
 * Whether "Re-run macOS benchmark" is offered (force `rebench-macos`, broker
 * private/auth publishRoutes.ts `macRebenchGuard` — keep the two in step):
 * ONLY the macOS arm64 benchmark of the already-published build is measured
 * again and merged into its stored record; no compile, no publish, no
 * pointer move. Offered when the registry serves a PUBLISHED build whose
 * darwin-arm64 package shipped and whose latest macOS result is missing,
 * unavailable / not measured, invalid or degraded (partial). Not while the
 * original measurement is still queued or running (nothing to repair yet),
 * and not when the Mac already produced a usable number (ok, or noisy —
 * noisy is published with a visible badge, not a failure).
 */
export function canRebenchMacos<T extends VersionRowLike>(
  state: VersionState<T>,
): PrimaryAction | null {
  if (state.kind !== "served") return null;
  const b = state.build;
  if (!b || b.status !== "published") return null;
  const darwin = parseNativeTargets(b.native_targets).find((t) =>
    t.platform.startsWith("darwin-"),
  );
  if (!darwin || darwin.status !== "shipped") return null;
  const row = parseSteps(b.steps)?.find((s) => s.id === "benchmark-mac");
  if (row && (row.state === "pending" || row.state === "running")) return null;
  const verdict = darwin.macBench?.state;
  const needsIt =
    verdict === "unavailable" ||
    verdict === "invalid" ||
    row?.state === "degraded" ||
    row?.state === "failed" ||
    // no verdict recorded and the checklist does not say ok either
    (verdict === undefined && row?.state !== "ok");
  if (!needsIt) return null;
  return {
    kind: "rebench-macos",
    label: "Re-run macOS benchmark",
    title:
      "Measure only the macOS arm64 benchmark of this published build again on the Mac and merge it into the stored result (no compile, no publish)",
  };
}

/** Plain-language confirmation for the primary action. */
export function confirmText<T extends VersionRowLike>(
  state: VersionState<T>,
  pkg: string,
  version: string,
): { title: string; body: string[] } {
  const target = `${pkg}@${version}`;
  switch (state.kind) {
    case "served":
      return {
        title: `Rebuild ${target} with the current compiler?`,
        body: [
          `This builds ${target} with the current compiler, runs the full test and verification pipeline, and — only if everything passes — publishes it as a new build ${version}-pure.<compiler> and points ${version} at it, so new installs of ${version} get it.`,
          ...rebuildLockfileNotes(pkg, version),
          ...(REBUILD_LOCKFILE_WARNING ? [REBUILD_LOCKFILE_WARNING] : []),
          `If any step fails, ${version} keeps pointing at the current build.`,
        ],
      };
    case "withdrawn":
      return {
        title: `Re-publish ${target} with the current compiler?`,
        body: [
          `${target} was rolled back: registry.pure.dev serves upstream's original package for it. This builds it with the current compiler, runs the full test and verification pipeline, and — only if everything passes — publishes a new build and points ${version} at it again.`,
          ...rebuildLockfileNotes(pkg, version),
          ...(REBUILD_LOCKFILE_WARNING ? [REBUILD_LOCKFILE_WARNING] : []),
        ],
      };
    case "unpublished":
      return {
        title: state.last
          ? `Retry publishing ${target}?`
          : `Publish ${target}?`,
        body: [
          `Runs the pipeline for ${target} with the current compiler: compile → test → compile → test → compile → verify → publish. Nothing is published unless every step passes.`,
        ],
      };
    case "in_progress":
      return {
        title: `${target} is already queued or in progress`,
        body: [],
      };
  }
}

/** Confirmation text of "Re-run macOS benchmark". */
export function rebenchConfirmText(
  pkg: string,
  version: string,
): { title: string; body: string[] } {
  const target = `${pkg}@${version}`;
  return {
    title: `Re-run the macOS benchmark of ${target}?`,
    body: [
      `Measures ONLY the macOS arm64 benchmark of the published build again, on the Mac (three runtime passes, roughly 15–25 minutes), and merges the result into the stored benchmark record. Nothing is compiled or published and ${version} keeps pointing at the same build; the glibc and musl results are not touched.`,
      `It is refused when no Mac is connected and healthy, or when another attempt is queued or running. A worse result never replaces a better stored one. Cancel stops it at any time.`,
    ],
  };
}

/** What the toast says once the broker accepted the request. */
export function queuedToast(
  kind: PipelineActionKind,
  stateKind: VersionState<VersionRowLike>["kind"],
  pkg: string,
  version: string,
): string {
  const target = `${pkg}@${version}`;
  if (kind === "rebench-macos")
    return `macOS benchmark re-run queued for ${target}: the Mac measures the published build again and, when it finishes, the result is merged into the stored benchmark (no compile, no publish).`;
  if (kind === "rebuild")
    return stateKind === "withdrawn"
      ? `Re-publish queued for ${target}: it will build with the current compiler and, if every step passes, ${version} will point at the new build again.`
      : `Rebuild queued for ${target}: it will build with the current compiler and, if every step passes, ${version} will point at the new build (older builds stay pinnable).`;
  return stateKind === "unpublished"
    ? `Publish queued for ${target}: compile → test → verify → publish with the current compiler.`
    : `Publish queued for ${target}.`;
}

/** Label for a queued/in-progress attempt's mode, e.g. "rebuild". */
export function attemptModeLabel(
  mode: string | null | undefined,
): string | null {
  switch (mode) {
    case "rebuild":
      return "rebuild";
    case "rebench-macos":
      return "macOS benchmark re-run";
    case "verify":
      return "re-verify (compiler release)";
    // The compiler-release sweep re-verifies only (default since the
    // moving-pointer scheme: X.Y.Z moves only on an operator Rebuild).
    case "publish":
      return "publish";
    default:
      return null;
  }
}

/** Deprecate/Roll back act only on a build the registry serves. */
export function canDeprecate<T extends VersionRowLike>(
  state: VersionState<T>,
): boolean {
  return state.kind === "served";
}
export const canRollBack = canDeprecate;

/** The deprecation dialog's submit does something only when the message
 *  differs from what is set ('' clears; clearing nothing is a no-op). */
export function deprecationChanges(
  current: string | null,
  next: string,
): boolean {
  return next.trim() !== (current ?? "");
}

/** A request the broker just accepted, shown on the page as a queued
 *  attempt until the publish service's own `pending` row arrives (NATS →
 *  deploy-agent → broker → D1 takes a moment) — so a click is never
 *  followed by "nothing visible happens". */
export interface LocalQueuedRequest {
  pkg: string;
  version: string;
  kind: PipelineActionKind;
  /** Client clock, ms. */
  at: number;
  requestedBy: string | null;
}

/** How long a local placeholder may stand in for the server's row. */
export const LOCAL_QUEUED_TTL_MS = 2 * 60_000;

/**
 * rows + a synthetic `pending` attempt for `queued`, unless the server
 * already shows the request (an in-progress row, or any row updated since
 * the click — tolerating a few seconds of clock skew) or the placeholder
 * has expired.
 */
export function withLocalQueued<T extends VersionRowLike>(
  rows: T[],
  queued: LocalQueuedRequest | undefined,
  now: number,
  make: (q: LocalQueuedRequest) => T,
): T[] {
  if (!queued || now - queued.at > LOCAL_QUEUED_TTL_MS) return rows;
  const reflected = rows.some(
    (r) =>
      !TERMINAL_STATUSES.has(r.status) ||
      parseTimestamp(r.updated_at) >= queued.at - 5_000,
  );
  return reflected ? rows : [make(queued), ...rows];
}
